// ═══════════════════════════════════════════════════════════════════════════
// IMPORT ACTIONS WIZARD — import d'actions depuis un fichier .xlsx (3 étapes)
// ═══════════════════════════════════════════════════════════════════════════
//
// 3 étapes : choix du projet + fichier .xlsx → validation des actions parsées
// → compléter approbateur et date butoir. Le parsing .xlsx est fait côté
// backend (POST /actions/import-xls). Étapes 2 et 3 alignées sur le wizard
// d'analyse de CR (page-meeting-wizard.ts).

interface IWUser {
  id: number;
  name: string;
  trigram?: string | null;
  is_team?: boolean;
}

interface IWAction {
  id: number;
  text?: string | null;
  approver_id?: number | null;
  approver_name?: string | null;
  performers?: number[];
  deadline?: string | null;
}

interface IWStatus {
  id: number;
  name: string;
}

interface IWProject {
  id: number;
  code?: string | null;
  name: string;
}

// Action parsée renvoyée par POST /actions/import-xls.
interface ParsedAction {
  text: string;
  priority: number;
  progress: number;
  deadline: string | null;
  deadline_revised: string | null;
  status_id: number | null;
  approver_id: number | null;
  performers: number[];
  consulted: number[];
  informed: number[];
  committee_ids: number[];
  warnings: string[];
}

interface ProposedAction {
  data: ParsedAction;
  status: 'validated' | 'cancelled';
}

interface IWResumeState {
  projectId?: string;
  createdActions?: IWAction[];
  users?: IWUser[];
  statuses?: IWStatus[];
  projects?: IWProject[];
  currentFilter?: 'all' | 'noresp' | 'nodate';
}

declare global {
  interface Window {
    showImportActionsWizard: (resumeState?: IWResumeState) => Promise<void>;
  }
}

window.showImportActionsWizard = async function (resumeState?: IWResumeState): Promise<void> {
  let step: 1 | 2 | 3 = resumeState ? 3 : 1;
  let projectId = resumeState?.projectId || '';
  let xlsFile: File | null = null;
  let proposed: ProposedAction[] = [];
  let createdActions: IWAction[] = resumeState?.createdActions || [];
  let users: IWUser[] = resumeState?.users || [];
  let statuses: IWStatus[] = resumeState?.statuses || [];
  let projects: IWProject[] = resumeState?.projects || [];
  let currentFilter: 'all' | 'noresp' | 'nodate' = resumeState?.currentFilter || 'all';

  if (!resumeState) {
    try {
      [users, statuses, projects] = (await Promise.all([
        loadUsers(),
        loadActionStatuses(),
        api.getProjects(),
      ])) as unknown as [IWUser[], IWStatus[], IWProject[]];
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      toast(t('importWizard.toast.refsError', { msg }), 'error');
      return;
    }
  }

  const userMap: Record<number, IWUser> = Object.fromEntries(users.map((u) => [u.id, u]));

  function projectName(id: string): string {
    const p = projects.find((x) => x.id === parseInt(id));
    return p ? `${p.code ? p.code + ' — ' : ''}${p.name}` : '';
  }

  // ── Étape 1 : projet + fichier ─────────────────────────────────────────────
  function renderStep1(): string {
    const projOpts = projects
      .map(
        (p) =>
          `<option value="${p.id}"${String(p.id) === String(projectId) ? ' selected' : ''}>${esc(p.code ? p.code + ' — ' : '')}${esc(p.name)}</option>`,
      )
      .join('');
    const fileLabel = xlsFile ? `📎 ${esc(xlsFile.name)}` : t('importWizard.step1.fileChoose');
    return `
      <div style="font-size:12px;color:var(--text3);margin-bottom:12px">
        ${esc(t('importWizard.step1.subtitle'))}
      </div>

      <div class="card" style="margin-bottom:12px">
        <div style="font-size:12px;font-weight:500;color:var(--text3);text-transform:uppercase;letter-spacing:.05em;margin-bottom:8px">${esc(t('importWizard.step1.project'))}</div>
        <select id="iw-project" style="width:100%;font-size:13px;padding:6px;border:1px solid var(--border2);border-radius:var(--radius)">
          <option value=""${projectId === '' ? ' selected' : ''}>${esc(t('importWizard.step1.projectPlaceholder'))}</option>
          ${projOpts}
        </select>
        <p style="font-size:11px;color:var(--text3);margin-top:6px;margin-bottom:0">
          ${esc(t('importWizard.step1.projectNote'))}
        </p>
      </div>

      <div class="card" style="margin-bottom:12px">
        <div style="font-size:12px;font-weight:500;color:var(--text3);text-transform:uppercase;letter-spacing:.05em;margin-bottom:8px">${esc(t('importWizard.step1.file'))}</div>
        <label class="btn btn-secondary btn-sm" style="cursor:pointer">
          ${esc(fileLabel)}
          <input type="file" id="iw-file" accept=".xlsx" style="display:none">
        </label>
        <p style="font-size:11px;color:var(--text3);margin-top:6px;margin-bottom:0">
          ${esc(t('importWizard.step1.fileNote'))}
        </p>
      </div>
    `;
  }

  function wireStep1(): void {
    const ps = document.getElementById('iw-project') as HTMLSelectElement | null;
    if (ps)
      ps.addEventListener('change', (e: Event) => {
        projectId = (e.target as HTMLSelectElement | null)?.value || '';
      });
    const fi = document.getElementById('iw-file') as HTMLInputElement | null;
    if (fi)
      fi.addEventListener('change', (e: Event) => {
        xlsFile = (e.target as HTMLInputElement | null)?.files?.[0] || null;
        refreshBody();
      });
  }

  async function gotoStep2(): Promise<void> {
    if (!projectId) {
      toast(t('importWizard.toast.projectRequired'), 'error');
      return;
    }
    if (!xlsFile) {
      toast(t('importWizard.toast.fileRequired'), 'error');
      return;
    }
    const bodyEl = document.querySelector<HTMLElement>('.modal-body');
    if (bodyEl) {
      bodyEl.innerHTML = `<div style="text-align:center;padding:32px 16px">
        <div style="font-size:13px;color:var(--text3);margin-bottom:16px">${esc(t('importWizard.btn.step2'))}</div>
        <div style="background:var(--border);border-radius:4px;height:6px;overflow:hidden;width:100%;max-width:320px;margin:0 auto">
          <div style="background:var(--accent);height:100%;width:60%;border-radius:4px;animation:iw-progress 1.2s ease-in-out infinite"></div>
        </div>
      </div>`;
      if (!document.getElementById('iw-progress-style')) {
        const s = document.createElement('style');
        s.id = 'iw-progress-style';
        s.textContent =
          '@keyframes iw-progress{0%{width:0%;margin-left:0}50%{width:60%;margin-left:20%}100%{width:0%;margin-left:100%}}';
        document.head.appendChild(s);
      }
    }
    let result: { actions?: ParsedAction[] };
    try {
      result = (await api.importActionsXls(xlsFile)) as typeof result;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      toast(t('importWizard.toast.parseError', { msg }), 'error');
      refreshAll();
      return;
    }
    const list = result?.actions || [];
    if (!list.length) {
      toast(t('importWizard.toast.noActions'), 'info');
      refreshAll();
      return;
    }
    proposed = list.map((a) => ({ data: a, status: 'validated' as const }));
    step = 2;
    refreshAll();
  }

  // ── Étape 2 : sélection des actions à créer ────────────────────────────────
  function renderStep2(): string {
    const validated = proposed.filter((p) => p.status === 'validated').length;
    return `
      <div style="font-size:12px;color:var(--text3);margin-bottom:10px">
        ${esc(t('importWizard.step2.subtitle'))}
      </div>
      <div style="font-size:12px;color:var(--text3);margin-bottom:10px">
        ${t('importWizard.step2.attachProject', { name: projectName(projectId) })}
      </div>
      <div style="margin-bottom:10px">
        <span style="font-size:13px;font-weight:500">${t('importWizard.step2.summary', { n: validated, total: proposed.length })}</span>
      </div>
      <div id="iw-actions-list">
        ${proposed.map((p) => renderProposedRow(p)).join('') || `<p style="color:var(--text3)">${esc(t('importWizard.step2.empty'))}</p>`}
      </div>
    `;
  }

  function renderProposedRow(p: ProposedAction): string {
    const i = proposed.indexOf(p);
    const isActive = p.status === 'validated';
    const borderColor = isActive ? 'var(--ok)' : 'var(--text3)';
    const bg = isActive ? 'rgba(22,163,74,0.05)' : 'var(--bg3)';
    const statusBadge = isActive
      ? `<span style="font-size:10px;padding:2px 8px;border-radius:10px;background:var(--ok);color:#fff;font-weight:600">${esc(t('importWizard.step2.badgeToCreate'))}</span>`
      : `<span style="font-size:10px;padding:2px 8px;border-radius:10px;background:var(--text3);color:#fff;font-weight:600">${esc(t('importWizard.step2.badgeWontCreate'))}</span>`;
    const rowOpacity = isActive ? '' : 'opacity:0.5;';
    const warns =
      p.data.warnings && p.data.warnings.length
        ? `<div style="font-size:11px;color:#b45309;margin-top:6px">⚠ ${esc(p.data.warnings.join(' · '))}</div>`
        : '';
    return `<div class="card" style="margin-bottom:6px;padding:10px 12px;border-left:4px solid ${borderColor};background:${bg};${rowOpacity}">
      <div style="display:flex;align-items:center;gap:10px;margin-bottom:6px">
        <label style="display:flex;align-items:center;gap:6px;cursor:pointer;font-size:12px;font-weight:500">
          <input type="checkbox" class="iw-toggle" data-idx="${i}"${isActive ? ' checked' : ''}>
          <span>${esc(t('importWizard.step2.createCheckbox'))}</span>
        </label>
        ${statusBadge}
      </div>
      <div style="display:flex;align-items:center;gap:10px">
        <input type="text" class="iw-label" data-idx="${i}" value="${esc(p.data.text)}"
          style="flex:1;font-size:13px;padding:6px 8px;border:1px solid var(--border2);border-radius:var(--radius)"${isActive ? '' : ' disabled'}>
      </div>
      ${warns}
    </div>`;
  }

  function wireStep2(): void {
    document.querySelectorAll<HTMLInputElement>('.iw-label').forEach((inp) => {
      inp.addEventListener('input', (e: Event) => {
        const target = e.target as HTMLInputElement | null;
        const i = parseInt(target?.dataset['idx'] || '0');
        const item = proposed[i];
        if (item) item.data.text = target?.value || '';
      });
    });
    document.querySelectorAll<HTMLInputElement>('.iw-toggle').forEach((cb) => {
      cb.addEventListener('change', (e: Event) => {
        const target = e.target as HTMLInputElement | null;
        const i = parseInt(target?.dataset['idx'] || '0');
        const item = proposed[i];
        if (item) {
          item.status = target?.checked ? 'validated' : 'cancelled';
          refreshStep2Body();
        }
      });
    });
  }

  function refreshStep2Body(): void {
    const body = document.getElementById('modal-body');
    if (body) body.innerHTML = renderStep2();
    wireStep2();
  }

  async function gotoStep3(): Promise<void> {
    const toCreate = proposed.filter((p) => p.status === 'validated');
    if (!toCreate.length) {
      toast(t('importWizard.toast.noValidated'), 'info');
      return;
    }
    createdActions = [];
    const failed: Array<{ label: string; error: string }> = [];
    for (const p of toCreate) {
      const d = p.data;
      const payload = {
        text: d.text.trim(),
        deadline: d.deadline,
        deadline_revised: d.deadline_revised,
        status_id: d.status_id,
        progress: d.progress || 0,
        approver_id: d.approver_id,
        performers: d.performers || [],
        consulted: d.consulted || [],
        informed: d.informed || [],
        priority: d.priority,
        project_ids: [parseInt(projectId)],
        committee_ids: d.committee_ids || [],
      };
      try {
        // notify:false — un import en masse ne doit pas déclencher une
        // notification par action créée.
        const created = (await api.createAction(payload, { notify: false })) as IWAction;
        createdActions.push(created);
      } catch (err: unknown) {
        failed.push({ label: d.text, error: err instanceof Error ? err.message : String(err) });
      }
    }
    if (failed.length) {
      toast(
        t('importWizard.toast.partialFail', { n: failed.length, total: toCreate.length }),
        'error',
      );
      safeWarn('[import-wizard] créations en échec', failed);
    }
    if (!createdActions.length) {
      toast(t('importWizard.toast.noCreated'), 'error');
      return;
    }
    toast(t('importWizard.toast.created', { n: createdActions.length }));
    step = 3;
    refreshAll();
  }

  // ── Étape 3 : compléter approbateur + date butoir ──────────────────────────
  function isComplete(a: IWAction): boolean {
    return !!(a.approver_id && a.deadline);
  }

  function filteredCreated(): IWAction[] {
    let v = createdActions;
    if (currentFilter === 'noresp') v = v.filter((a) => !a.approver_id);
    if (currentFilter === 'nodate') v = v.filter((a) => !a.deadline);
    return v;
  }

  function renderProgress(): string {
    const total = createdActions.length;
    const complete = createdActions.filter(isComplete).length;
    const pct = total ? Math.round((complete / total) * 100) : 0;
    const col = pct >= 100 ? 'var(--ok)' : pct >= 50 ? 'var(--accent)' : 'var(--warn)';
    return `<div style="margin-bottom:12px">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:6px">
        <div style="font-size:12px;font-weight:500;color:var(--text3);text-transform:uppercase;letter-spacing:.05em">${esc(t('importWizard.step3.progress'))}</div>
        <span style="font-size:13px;font-weight:500">${t('importWizard.step3.progressValue', { complete, total, pct })}</span>
      </div>
      <div style="height:8px;background:var(--bg3);border-radius:4px;overflow:hidden">
        <div style="height:100%;width:${pct}%;background:${col};transition:width .3s"></div>
      </div>
    </div>`;
  }

  function renderFiltersStep3(): string {
    const opts: Array<[typeof currentFilter, string]> = [
      ['all', t('importWizard.step3.filterAll')],
      ['noresp', t('importWizard.step3.filterNoresp')],
      ['nodate', t('importWizard.step3.filterNodate')],
    ];
    const count = filteredCreated().length;
    return `<div style="display:flex;align-items:center;gap:6px;margin-bottom:8px;flex-wrap:wrap">
      <div id="iw-actions-count" style="font-size:12px;font-weight:500;color:var(--text3);text-transform:uppercase;letter-spacing:.05em;flex:1;min-width:140px">${t('importWizard.step3.actionsCount', { n: count })}</div>
      ${opts.map(([k, lbl]) => `<button type="button" class="btn btn-secondary btn-sm iw-filter${k === currentFilter ? ' active' : ''}" data-bf="${k}">${esc(lbl)}</button>`).join('')}
    </div>`;
  }

  function renderCreatedRow(a: IWAction): string {
    const approverName = a.approver_id
      ? userMap[a.approver_id]?.name || a.approver_name || '—'
      : '—';
    const deadline = a.deadline ? fmtDate(a.deadline) : '—';
    const statusColor = isComplete(a)
      ? 'var(--ok)'
      : a.approver_id || (a.performers || []).length || a.deadline
        ? 'var(--warn)'
        : 'var(--text3)';
    const dot = `<span style="width:8px;height:8px;border-radius:50%;background:${statusColor};display:inline-block;margin-right:4px"></span>`;
    return `<div class="card" style="margin-bottom:6px;cursor:pointer;padding:10px 12px" data-iw-action-id="${a.id}">
      <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:12px">
        <div style="flex:1">
          <div style="font-size:13px;font-weight:500">${esc(a.text || '')}</div>
          <div style="display:flex;gap:14px;margin-top:6px;font-size:12px;color:var(--text3)">
            <span>${t('importWizard.step3.respLabel', { name: approverName })}</span>
            <span>${t('importWizard.step3.deadlineLabel', { date: deadline })}</span>
          </div>
        </div>
        <div style="display:flex;align-items:center;gap:6px;flex-shrink:0">
          ${dot}
          <button type="button" class="btn btn-secondary btn-icon iw-edit-action" data-iw-edit="${a.id}" title="${esc(t('common.edit'))}" aria-label="${esc(t('common.edit'))}" style="font-size:13px">✎</button>
        </div>
      </div>
    </div>`;
  }

  function renderActionsListStep3(): string {
    const v = filteredCreated();
    if (!v.length)
      return `<p style="color:var(--text3);font-size:12px;padding:16px 0">${esc(t('importWizard.step3.empty'))}</p>`;
    return v.map(renderCreatedRow).join('');
  }

  function renderStep3(): string {
    return `
      <div style="font-size:12px;color:var(--text3);margin-bottom:10px">
        ${esc(t('importWizard.step3.subtitle'))}
      </div>
      ${renderProgress()}
      ${renderFiltersStep3()}
      <div id="iw-created-list">${renderActionsListStep3()}</div>
    `;
  }

  function refreshStep3InstancesOnly(): void {
    const el = document.getElementById('iw-created-list');
    if (el) el.innerHTML = renderActionsListStep3();
    const countEl = document.getElementById('iw-actions-count');
    if (countEl)
      countEl.textContent = t('importWizard.step3.actionsCount', { n: filteredCreated().length });
    wireStep3Instances();
  }

  function wireStep3Instances(): void {
    document.querySelectorAll<HTMLButtonElement>('.iw-edit-action').forEach((btn) => {
      btn.addEventListener('click', (e: Event) => {
        e.stopPropagation();
        void (async () => {
          const aid = parseInt(btn.dataset['iwEdit'] || '0');
          const action = createdActions.find((x) => x.id === aid);
          if (!action) return;
          let fresh: IWAction | undefined;
          try {
            // GET /actions/{id} : charge la seule action éditée, pas toute la
            // table (renvoie aussi les drafts to_validate du wizard).
            fresh = (await api.getAction(aid)) as IWAction;
          } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            toast(t('importWizard.toast.loadActionError', { msg }), 'error');
            return;
          }
          if (!fresh) {
            toast(t('importWizard.toast.actionNotFound'), 'error');
            return;
          }
          window.showEditActionModal(
            fresh as never,
            {
              users: users as never,
              statuses: statuses as never,
              projects: projects as never,
              onSaved: async () => {
                let refreshed = createdActions;
                try {
                  // Seule l'action `aid` a changé : on ne recharge qu'elle.
                  const fresh2 = (await api.getAction(aid)) as IWAction;
                  refreshed = createdActions.map((a) => (a.id === aid ? fresh2 : a));
                } catch {
                  /* non bloquant */
                }
                await window.showImportActionsWizard({
                  users,
                  statuses,
                  projects,
                  projectId,
                  createdActions: refreshed,
                  currentFilter,
                });
              },
              // Fermer/Annuler la modale d'édition revient au wizard (étape 3)
              // au lieu de retomber sur la liste #actions : les actions sont
              // déjà créées, l'utilisateur poursuit sa relecture.
              onCancel: () => {
                void window.showImportActionsWizard({
                  users,
                  statuses,
                  projects,
                  projectId,
                  createdActions,
                  currentFilter,
                });
              },
            } as never,
          );
        })();
      });
    });
  }

  function wireStep3(): void {
    document.querySelectorAll<HTMLButtonElement>('.iw-filter').forEach((btn) => {
      btn.addEventListener('click', () => {
        currentFilter = (btn.dataset['bf'] || 'all') as typeof currentFilter;
        document
          .querySelectorAll<HTMLButtonElement>('.iw-filter')
          .forEach((b) => b.classList.toggle('active', b.dataset['bf'] === currentFilter));
        refreshStep3InstancesOnly();
      });
    });
    wireStep3Instances();
  }

  // ── Orchestration modale ───────────────────────────────────────────────────
  function modalBody(): string {
    if (step === 1) return renderStep1();
    if (step === 2) return renderStep2();
    return renderStep3();
  }

  function modalTitle(): string {
    return t('importWizard.title', { n: step });
  }

  function refreshBody(): void {
    const body = document.getElementById('modal-body');
    if (body) body.innerHTML = modalBody();
    wireBody();
  }

  function refreshAll(): void {
    const title = document.getElementById('modal-title');
    if (title) title.textContent = modalTitle();
    refreshBody();
    updateFooter();
  }

  function wireBody(): void {
    if (step === 1) wireStep1();
    else if (step === 2) wireStep2();
    else wireStep3();
  }

  function updateFooter(): void {
    const confirmBtn = document.getElementById('modal-confirm') as HTMLButtonElement | null;
    if (confirmBtn) {
      if (step === 1) confirmBtn.textContent = t('importWizard.btn.step2');
      else if (step === 2) confirmBtn.textContent = t('importWizard.btn.step3');
      else confirmBtn.textContent = t('importWizard.btn.finish');
    }
    const cancelBtn = document.getElementById('modal-cancel') as HTMLButtonElement | null;
    if (cancelBtn) cancelBtn.style.display = step === 3 ? 'none' : '';
  }

  async function onConfirm(): Promise<void> {
    if (step === 1) return gotoStep2();
    if (step === 2) return gotoStep3();
    closeModal();
    const targetHash = '#actions?all=1';
    if (window.location.hash === targetHash) {
      if (typeof router?.dispatch === 'function') {
        router.dispatch();
      }
    } else {
      window.location.hash = targetHash;
    }
  }

  const initialStep: number = step;
  const initialLabel =
    initialStep === 1
      ? t('importWizard.btn.step2')
      : initialStep === 2
        ? t('importWizard.btn.step3')
        : t('importWizard.btn.finish');
  openWideModal(modalTitle(), modalBody(), onConfirm, initialLabel);
  setTimeout(() => {
    wireBody();
    updateFooter();
  }, 0);
};

export {};
